import React, { useEffect, useState } from "react";
import { useApp } from "../contexts/AppContext";
import { useToast } from "../contexts/ToastContext";
import {
  server_post_data,
  save_funzone_settings,
  get_funzone_settings,
  download_printer_setup,
} from "../ServiceConnection/serviceconnection.js";
import { Download } from "lucide-react";

const cleanNumeric = (value, min = 0) => {
  if (value === "" || value === null) return "";

  // remove non-digits
  let v = value.toString().replace(/\D+/g, "");

  // remove leading zeros
  v = v.replace(/^0+(?=\d)/, "");

  if (v === "") v = "0";

  v = Number(v);

  if (v < min) v = min;

  return v;
};
const Field = ({ label, dot, children }) => (
  <div className="space-y-2">
    <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
      {dot && <span className={`h-3 w-3 rounded-full ${dot}`} />}
      {label}
    </label>
    {children}
  </div>
);

const inputCls =
  "w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 disabled:bg-gray-50 disabled:text-gray-400";

export default function SettingsPage() {
  const { settings, updateSettings } = useApp();
  const { toast } = useToast();

  // FIX 1: Single source of truth — settings_id track karo for update
  const [settingsId, setSettingsId] = useState("0");

  const upd = (k, min = 0) => (e) => {
  const v = cleanNumeric(e.target.value, min);
  updateSettings({ [k]: v });
};
  const updText = (k) => (e) => updateSettings({ [k]: e.target.value });

  /* ---------------- LOAD SETTINGS ---------------- */
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await server_post_data(
          get_funzone_settings,
          new FormData(),
        );

        if (response.data.status === 1) {
          const data = response.data.data;

          // FIX 2: Track primary_id for update (not always "0")
          setSettingsId(data.primary_id ? String(data.primary_id) : "0");

          // FIX 3: Single updateSettings call — printer state alag nahi, sab context mein
          updateSettings({
            defaultPricePerHour: data.default_price_per_hour,
            defaultDuration: data.default_duration_minutes,
            greenThreshold: data.green_until_minutes,
            orangeThreshold: data.orange_until_minutes,
            gstPercentage: data.gst_percentage || 0,
            printerName: data.printer_name || "",
            printerType: data.printer_type || "LAN",
            printerIP: data.printer_ip || "",
            printerPort: data.printer_port || 9100,
            printerWidth: data.printer_width_mm || 80,
          });
        }
      } catch (error) {
        console.log("Settings fetch error", error);
      }
    };

    fetchSettings();
  }, [updateSettings]);

  /* ---------------- SAVE SETTINGS ---------------- */
  const handleSave = async () => {
    try {
      const formData = new FormData();

      // FIX 4: main_id = existing id for update, "0" only for first-time create
      formData.append("main_id", settingsId);

      formData.append("default_price_per_hour", settings.defaultPricePerHour);
      formData.append("default_duration_minutes", settings.defaultDuration);
      formData.append("green_until_minutes", settings.greenThreshold);
      formData.append("orange_until_minutes", settings.orangeThreshold);
      formData.append("gst_percentage", settings.gstPercentage);
      // FIX 5: Single source — settings context se, no duplicate appends
      formData.append("printer_name", settings.printerName || "");
      formData.append("printer_type", settings.printerType || "LAN");
      formData.append("printer_ip", settings.printerIP || "");
      formData.append("printer_port", settings.printerPort || 9100);
      formData.append("printer_width_mm", settings.printerWidth || 80); // FIX 6: correct key name

      const response = await server_post_data(save_funzone_settings, formData);

      if (response.data.status === 1) {
        // FIX 7: After create (main_id=0), update settingsId with new id
        if (settingsId === "0" && response.data.settings_id) {
          setSettingsId(String(response.data.settings_id));
        }

        toast({
          title: "Settings Saved ✅",
          description: "All configuration saved successfully",
        });
      } else {
        toast({ title: "Error ❌", description: "Unable to save settings" });
      }
    } catch (error) {
      console.log(error);
      toast({ title: "Error ❌", description: "Unable to save settings" });
    }
  };

  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-gray-900">
        Settings
      </h1>
      <p className="mb-6 text-gray-500">
        Manage pricing and time configuration
      </p>

      <div className="max-w-lg space-y-6">
        {/* ── Pricing ── */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <h2 className="font-display text-lg font-bold text-gray-900">
            Pricing
          </h2>

          <Field label="Default Price per Hour (₹)">
            <input
              type="number"
              value={settings.defaultPricePerHour}
              onChange={upd("defaultPricePerHour",1)}
              className={inputCls}
            />
          </Field>

          <Field label="Default Duration (minutes)">
            <input
              type="number"
              value={settings.defaultDuration}
              onChange={upd("defaultDuration",1)}
              className={inputCls}
            />
          </Field>
          <Field label="GST Percentage">
            <select
              value={settings.gstPercentage || 0}
              onChange={updText("gstPercentage")}
              className={inputCls}
            >
              <option value={0}>0%</option>
              <option value={5}>5%</option>
              <option value={12}>12%</option>
              <option value={18}>18%</option>
              <option value={27}>27%</option>
            </select>
          </Field>
        </div>

        {/* ── Time Thresholds ── */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <div>
            <h2 className="font-display text-lg font-bold text-gray-900">
              Time Thresholds
            </h2>
            <p className="text-sm text-gray-500">
              Configure color-coded alert timing
            </p>
          </div>

          <Field label="Green until (minutes)" dot="bg-green-500">
            <input
              type="number"
              value={settings.greenThreshold}
              onChange={upd("greenThreshold",1)}
              className={inputCls}
            />
          </Field>

          <Field label="Orange until (minutes)" dot="bg-orange-500">
            <input
              type="number"
              value={settings.orangeThreshold}
              onChange={upd("orangeThreshold",1)}
              className={inputCls}
            />
          </Field>

          <Field label="Red after (minutes)" dot="bg-red-500">
            <input
              type="number"
              value={settings.orangeThreshold}
              disabled
              className={inputCls}
            />
            <p className="text-xs text-gray-400">
              Automatically matches orange threshold
            </p>
          </Field>
        </div>

        {/* ── Printer Configuration ── FIX 8: Single section only, alag printer state hata diya */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <div>
            <h2 className="font-display text-lg font-bold text-gray-900">
              Printer Configuration
            </h2>
            <p className="text-sm text-gray-500">
              Configure billing printer settings
            </p>
          </div>

          <Field label="Printer Name">
            <input
              type="text"
              value={settings.printerName || ""}
              onChange={updText("printerName")}
              placeholder="Billing Printer"
              className={inputCls}
            />
          </Field>

          <Field label="Printer Type">
            <select
              value={settings.printerType || "LAN"}
              onChange={updText("printerType")}
              className={inputCls}
            >
              <option value="LAN">LAN</option>
              <option value="USB">USB</option>
            </select>
          </Field>

          {settings.printerType === "LAN" && (
            <Field label="Printer IP Address">
              <input
                type="text"
                value={settings.printerIP || ""}
                onChange={updText("printerIP")}
                placeholder="192.168.1.100"
                className={inputCls}
              />
            </Field>
          )}

          {settings.printerType === "LAN" && (
            <Field label="Printer Port">
              <input
                type="number"
                min={0}
                value={settings.printerPort || 9100}
                onChange={upd("printerPort",1)}
                className={inputCls}
              />
            </Field>
          )}

          <Field label="Paper Width (mm)">
            <input
              type="number"
              min={0}
              value={settings.printerWidth || 80}
              onChange={upd("printerWidth",1)}
              className={inputCls}
            />
            <p className="text-xs text-gray-400">Default receipt size 80 mm</p>
          </Field>
        </div>

        {/* ── Printer Download ── */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-lg font-bold text-gray-900">
                Printer Setup
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Download and install the thermal printer driver
              </p>
            </div>
            <a
              href={download_printer_setup}
              download
              className="flex items-center gap-2 rounded-xl bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-purple-700 transition-colors shadow-sm"
            >
              <Download size={16} />
              Download .exe
            </a>
          </div>
        </div>

        {/* ── Save ── */}
        <button
          onClick={handleSave}
          className="w-full rounded-xl bg-purple-600 py-3 font-semibold text-white hover:bg-purple-700 transition-colors shadow-md"
        >
          Save Settings
        </button>
      </div>
    </div>
  );
}
